/**
 * Spatial Navigation Store
 *
 * Manages campus navigation state including current location, navigation history,
 * and location unlock progression.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createSyncMiddleware, WithSyncControls } from './middleware/syncMiddleware';
import {
  CampusLocation,
  NavigationHistoryItem,
  TransitionPreferences,
  SelectedBuildingState,
} from '../types/spatial';

interface SpatialStore extends WithSyncControls {
  // Navigation State
  currentLocation: CampusLocation;
  previousLocation: CampusLocation | null;
  locationHistory: NavigationHistoryItem[];
  isTransitioning: boolean;

  // Building Selection State
  selectedBuilding: SelectedBuildingState;

  // Unlock State
  unlockedLocations: CampusLocation[];

  // Transition Preferences
  transitionPreferences: TransitionPreferences;

  // Navigation Actions
  navigateToLocation: (location: CampusLocation) => void;
  navigateBack: () => void;
  setTransitioning: (transitioning: boolean) => void;

  // Building Selection Actions
  selectBuilding: (location: CampusLocation | null) => void;
  hoverBuilding: (location: CampusLocation | null) => void;

  // Unlock Actions
  unlockLocation: (location: CampusLocation) => void;
  unlockMultipleLocations: (locations: CampusLocation[]) => void;
  isLocationUnlocked: (location: CampusLocation) => boolean;

  // Preference Actions
  updateTransitionPreferences: (prefs: Partial<TransitionPreferences>) => void;

  // Reset
  resetSpatialState: () => void;

  // Hydration for save/load
  hydrateFromSave: (data: SpatialSaveData) => void;
}

/**
 * Data structure for save/load persistence
 */
export interface SpatialSaveData {
  currentLocation: CampusLocation;
  unlockedLocations: CampusLocation[];
  transitionPreferences?: TransitionPreferences;
}

// Default transition preferences
const defaultTransitionPreferences: TransitionPreferences = {
  enabled: true,
  reducedMotion: false,
  defaultType: 'fade',
  defaultDuration: 300,
};

// Default selected building state
const defaultSelectedBuilding: SelectedBuildingState = {
  location: null,
  isHovered: false,
  stats: null,
};

// Locations unlocked by default at game start
const defaultUnlockedLocations: CampusLocation[] = [
  CampusLocation.CAMPUS_OVERVIEW,
  CampusLocation.HEADQUARTERS,
  CampusLocation.DESIGN_STUDIO,
];

// Maximum history length to prevent memory issues
const MAX_HISTORY_LENGTH = 50;

// Partialize function for selective persistence
const partializeSpatialStore = (state: SpatialStore): SpatialSaveData => {
  return {
    currentLocation: state.currentLocation,
    unlockedLocations: state.unlockedLocations,
    transitionPreferences: state.transitionPreferences,
  };
};

// Sync configuration for spatial store
const spatialSyncConfig = {
  routing: {
    rules: [
      {
        match: () => true,
        target: 'localStorage' as const,
        priority: 100,
      },
    ],
  },
  behavior: {
    debounceDelay: 500, // Debounce navigation changes
    batchSize: 10,
    enableAutoSync: true,
    syncOnReconnect: false,
  },
  hooks: {
    beforeSync: (data: SpatialStore) => {
      return partializeSpatialStore(data);
    },
    afterSync: (result: { success: boolean }) => {
      if (result.success) {
        // console.debug('[SpatialStore] State saved');
      }
    },
    onError: (error: Error) => {
      console.warn('[SpatialStore] Sync error:', error.message);
    },
  },
  monitoring: {
    trackMetrics: false,
    reportInterval: 300000,
  },
};

export const useSpatialStore = create<SpatialStore>()(
  devtools(
    createSyncMiddleware('spatialStore', spatialSyncConfig)(
      (set, get) => ({
        // Initial State
        currentLocation: CampusLocation.CAMPUS_OVERVIEW,
        previousLocation: null,
        locationHistory: [],
        isTransitioning: false,
        selectedBuilding: defaultSelectedBuilding,
        unlockedLocations: [...defaultUnlockedLocations],
        transitionPreferences: defaultTransitionPreferences,

        // Navigation Actions
        navigateToLocation: (location) => {
          const state = get();

          // Don't navigate to current location
          if (state.currentLocation === location) {
            return;
          }

          // Check if location is unlocked
          if (!state.unlockedLocations.includes(location)) {
            console.warn(
              `[SpatialStore] Cannot navigate to locked location: ${location}`
            );
            return;
          }

          // Create history entry
          const historyItem: NavigationHistoryItem = {
            location: state.currentLocation,
            timestamp: Date.now(),
          };

          // Update history (limit length)
          const newHistory = [...state.locationHistory, historyItem].slice(
            -MAX_HISTORY_LENGTH
          );

          set(
            {
              previousLocation: state.currentLocation,
              currentLocation: location,
              locationHistory: newHistory,
              selectedBuilding: defaultSelectedBuilding, // Clear selection on navigate
            },
            false,
            `navigateToLocation:${location}`
          );
        },

        navigateBack: () => {
          const state = get();

          if (!state.previousLocation) {
            // If no previous location, go to campus overview
            if (state.currentLocation !== CampusLocation.CAMPUS_OVERVIEW) {
              set(
                {
                  previousLocation: state.currentLocation,
                  currentLocation: CampusLocation.CAMPUS_OVERVIEW,
                  selectedBuilding: defaultSelectedBuilding,
                },
                false,
                'navigateBack:toCampus'
              );
            }
            return;
          }

          set(
            {
              previousLocation: null,
              currentLocation: state.previousLocation,
              selectedBuilding: defaultSelectedBuilding,
            },
            false,
            'navigateBack'
          );
        },

        setTransitioning: (transitioning) =>
          set({ isTransitioning: transitioning }, false, 'setTransitioning'),

        // Building Selection Actions
        selectBuilding: (location) =>
          set(
            (state) => ({
              selectedBuilding: {
                ...state.selectedBuilding,
                location,
                isHovered: false,
              },
            }),
            false,
            `selectBuilding:${location}`
          ),

        hoverBuilding: (location) =>
          set(
            (state) => ({
              selectedBuilding: {
                ...state.selectedBuilding,
                location: location ?? state.selectedBuilding.location,
                isHovered: location !== null,
              },
            }),
            false,
            'hoverBuilding'
          ),

        // Unlock Actions
        unlockLocation: (location) => {
          const state = get();
          if (state.unlockedLocations.includes(location)) {
            return; // Already unlocked
          }

          set(
            {
              unlockedLocations: [...state.unlockedLocations, location],
            },
            false,
            `unlockLocation:${location}`
          );
        },

        unlockMultipleLocations: (locations) => {
          const state = get();
          const newLocations = locations.filter(
            (loc) => !state.unlockedLocations.includes(loc)
          );

          if (newLocations.length === 0) {
            return; // All already unlocked
          }

          set(
            {
              unlockedLocations: [...state.unlockedLocations, ...newLocations],
            },
            false,
            'unlockMultipleLocations'
          );
        },

        isLocationUnlocked: (location) => {
          return get().unlockedLocations.includes(location);
        },

        // Preference Actions
        updateTransitionPreferences: (prefs) =>
          set(
            (state) => ({
              transitionPreferences: {
                ...state.transitionPreferences,
                ...prefs,
              },
            }),
            false,
            'updateTransitionPreferences'
          ),

        // Reset
        resetSpatialState: () =>
          set(
            {
              currentLocation: CampusLocation.CAMPUS_OVERVIEW,
              previousLocation: null,
              locationHistory: [],
              isTransitioning: false,
              selectedBuilding: defaultSelectedBuilding,
              unlockedLocations: [...defaultUnlockedLocations],
              transitionPreferences: defaultTransitionPreferences,
            },
            false,
            'resetSpatialState'
          ),

        // Hydration for save/load
        hydrateFromSave: (data) => {
          set(
            {
              currentLocation: data.currentLocation,
              unlockedLocations: data.unlockedLocations,
              transitionPreferences:
                data.transitionPreferences ?? defaultTransitionPreferences,
              // Clear transient state
              previousLocation: null,
              locationHistory: [],
              isTransitioning: false,
              selectedBuilding: defaultSelectedBuilding,
            },
            false,
            'hydrateFromSave'
          );
        },
      })
    ),
    {
      name: 'spatial-store',
    }
  )
);

// Selectors
export const selectCurrentLocation = (state: SpatialStore) =>
  state.currentLocation;
export const selectPreviousLocation = (state: SpatialStore) =>
  state.previousLocation;
export const selectLocationHistory = (state: SpatialStore) =>
  state.locationHistory;
export const selectIsTransitioning = (state: SpatialStore) =>
  state.isTransitioning;
export const selectSelectedBuilding = (state: SpatialStore) =>
  state.selectedBuilding;
export const selectUnlockedLocations = (state: SpatialStore) =>
  state.unlockedLocations;
export const selectTransitionPreferences = (state: SpatialStore) =>
  state.transitionPreferences;

// Computed selectors
export const selectIsAtCampusOverview = (state: SpatialStore) =>
  state.currentLocation === CampusLocation.CAMPUS_OVERVIEW;

export const selectCanNavigateBack = (state: SpatialStore) =>
  state.previousLocation !== null ||
  state.currentLocation !== CampusLocation.CAMPUS_OVERVIEW;

export const selectBreadcrumbTrail = (state: SpatialStore) => {
  const trail = state.locationHistory.slice(-5).map((item) => item.location);
  if (!trail.includes(state.currentLocation)) {
    trail.push(state.currentLocation);
  }
  return trail;
};

export const selectIsLocationUnlocked =
  (location: CampusLocation) => (state: SpatialStore) =>
    state.unlockedLocations.includes(location);

export const selectLockedLocations = (state: SpatialStore) => {
  const allLocations = Object.values(CampusLocation);
  return allLocations.filter((loc) => !state.unlockedLocations.includes(loc));
};
